import React from "react";
import { ReferenceField, SelectInput, TextField } from "react-admin";

import { ReferenceEditableField } from "../../../components/fields/EditableField";
import { BooleanField } from "../../../components/fields/EqualField";
import { LongTextField } from "../../../components/fields/LongTextField";
import { ObjectChipField } from "../../../components/fields/ObjectChipField";
import { OrderStatusField } from "../../../components/fields/OrderStatusField";
import { EntityType, OrderServices, TaskServices } from "../../../entities";
import { useTerminalFieldVisibility } from "../../../hooks/useTerminalFieldVisibility";

export const MatchStatusField = ({ permissions, isAppManager, isOSFCUser, ...rest }) => {
	if (!rest.record) return null;

	if (rest.record.type === EntityType.order) {
		return (
			<OrderStatusField
				source="status"
				optimized={true}
				editable={isAppManager}
				permissions={permissions}
				additionalParams={["set_order_completed"]}
				{...rest}
			/>
		);
	}
	if (rest.record.type === EntityType.task) {
		return (
			<ReferenceEditableField
				optimized={true}
				editable={isAppManager}
				permissions={permissions}
				source="status"
				reference="task-statuses"
				additionalParams={["set_order_completed"]}
				editField={<SelectInput optionText="name" />}
				{...rest}
			>
				<TextField source="status_name" />
			</ReferenceEditableField>
		);
	}
	return null;
};

const servicesByEntityType = {
	[EntityType.order]: OrderServices,
	[EntityType.task]: TaskServices,
};

export const MatchServiceField = (props) => {
	if (!props.record) return null;

	return (
		<ObjectChipField
			source="service"
			object={servicesByEntityType[props.record.type]}
			titleKey="short_name"
			tooltipKey="full_name"
			{...props}
		/>
	);
};

export const MatchCompanyField = (props) => {
	if (!props.record) return null;
	if (props.record.type === EntityType.order) {
		return (
			<ReferenceField reference="companies" source="company_id" link={false} {...props}>
				<TextField source="full_name" />
			</ReferenceField>
		);
	}
	if (props.record.type === EntityType.task) {
		const staticRecord = { company: "OSFC" };
		return <TextField source="company" {...props} record={staticRecord} />;
	}
};

const notesSourceByEntityType = {
	[EntityType.order]: "notes",
	[EntityType.task]: "description",
};

export const MatchNotesField = (props) => {
	if (!props.record) return null;
	return (
		<LongTextField
			source={notesSourceByEntityType[props.record.type]}
			sortable={false}
			{...props}
		/>
	);
};

const internalNotesSourceByEntityType = {
	[EntityType.order]: "internal_notes",
	[EntityType.task]: "notes",
};

export const MatchInternalNotesField = (props) => {
	if (!props.record) return null;
	return (
		<LongTextField
			source={internalNotesSourceByEntityType[props.record.type]}
			sortable={false}
			{...props}
		/>
	);
};

export const OutgoingTripNumberOrIsRecurringField = (props) => {
	const { terminalId } = props;
	const { isFieldHidden } = useTerminalFieldVisibility(terminalId);

	if (!props.record) return null;

	// For orders, check if outgoing_trip_number field is hidden
	if (props.record.type === EntityType.order && isFieldHidden("outgoing_trip_number")) {
		return null;
	}

	// For tasks, show is_recurring
	if (props.record.type === EntityType.task) {
		const modifiedRecord = { ...props.record, is_recurring: !!props.record.recurring_task_id };
		return (
			<BooleanField source="is_recurring" {...props} record={modifiedRecord} sortable={false} />
		);
	}

	// For orders, show outgoing_trip_number
	return <TextField source="outgoing_trip_number" {...props} sortable={false} />;
};
